<template>
  <PageHeader title="Liste de vos marques" class="mb-4" />
  
  <div class="w-full px-6">
    <div class="mb-6 flex flex-wrap items-center gap-3">
      <ActionButton
        label="Ajouter une nouvelle marque"
        icon="fas fa-plus"
        variant="primary"
        @click="openAddBrandModal"
      />
      <ActionButton
        label="Activer les marques"
        icon="fas fa-check"
        variant="success"
        :disabled="brands.length === 0 || isActivating"
        :loading="isActivating"
        @click="handleActivateBrands"
      />
    </div>

    <!-- Desktop: Table view -->
    <div v-if="brands.length > 0" class="hidden md:block bg-white rounded-lg border border-gray-200 shadow-sm">
      <ReusableTable
        :columns="brandColumns"
        :data="brands"
        :sortable="true"
        :show-edit="true"
        :show-delete="true"
      >
        <template #column-isDefault="{ row }">
          <span class="text-center">
            {{ row.isDefault ? 'Oui' : 'Non' }}
          </span>
        </template>

        <template #column-isActive="{ row }">
          <span
            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
            :class="row.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-black'"
          >
            {{ row.isActive ? 'Activée' : 'Désactivée' }}
          </span>
        </template>

        <template #column-productCount="{ row }">
          <span
            class="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full text-sm font-medium"
            :class="(row.productCount ?? 0) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-black'"
          >
            {{ row.productCount ?? 0 }}
          </span>
        </template>

        <template #actions="{ row }">
          <div class="flex items-center gap-2">
            <BrandStatusSwitch
              :active="Boolean(row.isActive)"
              :disabled="statutUpdatingId === String(row.id)"
              @toggle="handleSetBrandStatus(row as unknown as Brand, !row.isActive)"
            />
            <button
              @click.stop="editBrand(row as unknown as Brand)"
              class="inline-flex h-8 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
              title="Modifier"
            >
              <i class="fas fa-pen text-[10px]"></i>
              Modifier
            </button>
            <button
              v-if="!row.isDefault"
              @click.stop="handleDeleteBrand(row as unknown as Brand)"
              class="inline-flex h-8 items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-100"
              title="Supprimer"
            >
              <i class="fas fa-times text-[10px]"></i>
              Supprimer
            </button>
            <span
              v-else
              class="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-xs font-medium text-gray-500"
              title="La marque par défaut ne peut pas être supprimée"
            >
              <i class="fas fa-lock text-[10px]"></i>
              Par défaut
            </span>
          </div>
        </template>
      </ReusableTable>
    </div>

    <!-- Mobile: Card view -->
    <div v-if="brands.length > 0" class="md:hidden space-y-3">
      <div
        v-for="brand in brands"
        :key="brand.id"
        class="bg-white rounded-lg border border-gray-200 shadow-sm p-4"
      >
        <div class="flex items-center justify-between gap-3 mb-3">
          <div class="min-w-0">
            <h3 class="text-base font-semibold text-black truncate">{{ brand.name }}</h3>
            <span
              v-if="brand.isDefault"
              class="mt-1 inline-flex px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium"
            >
              Par défaut
            </span>
          </div>
          <BrandStatusSwitch
            :active="brand.isActive"
            :disabled="statutUpdatingId === brand.id"
            @toggle="handleSetBrandStatus(brand, !brand.isActive)"
          />
        </div>

        <div class="flex items-center justify-between text-sm text-black mb-4">
          <span>Statut</span>
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            :class="brand.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-black'"
          >
            {{ brand.isActive ? 'Activée' : 'Désactivée' }}
          </span>
        </div>

        <div class="flex items-center justify-between text-sm text-black mb-4">
          <span>Nombre de produits</span>
          <span
            class="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-sm font-medium"
            :class="(brand.productCount ?? 0) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-black'"
          >
            {{ brand.productCount ?? 0 }}
          </span>
        </div>

        <div class="flex items-center gap-2">
          <button
            @click="editBrand(brand)"
            class="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 text-xs font-medium text-blue-700"
          >
            <i class="fas fa-pen text-[10px]"></i>
            Modifier
          </button>
          <button
            v-if="!brand.isDefault"
            @click="handleDeleteBrand(brand)"
            class="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 text-xs font-medium text-red-600"
          >
            <i class="fas fa-times text-[10px]"></i>
            Supprimer
          </button>
          <span
            v-else
            class="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-xs font-medium text-gray-500"
          >
            <i class="fas fa-lock text-[10px]"></i>
            Par défaut
          </span>
        </div>
      </div>
    </div>

    <div v-else class="bg-white rounded-lg border border-gray-200 p-8 text-center">
      <p class="text-black">Aucune marque trouvée.</p>
    </div>

    <ModalComp
      :isVisible="showModal"
      :title="modalTitle"
      size="md"
      @update:isVisible="showModal = false"
    >
      <template #body>
        <form @submit.prevent="handleSubmit" class="space-y-4">
          <BaseInput
            v-model="newBrand.name"
            label="Nom de la marque"
            required
          />
          <div class="flex items-center space-x-2">
            <BaseInput
              v-model="newBrand.isDefault"
              label="Marque par défaut"
              type="checkbox"
            />
          </div>
        </form>
      </template>
      
      <template #footer>
        <div class="flex justify-end gap-3">
          <ActionButton
            label="Annuler"
            variant="secondary"
            @click="showModal = false"
          />
          <ActionButton
            label="Enregistrer"
            variant="primary"
            :disabled="isSubmitting || !newBrand.name.trim()"
            :loading="isSubmitting"
            @click="handleSubmit"
          />
        </div>
      </template>
    </ModalComp>

    <ModalComp
      :isVisible="showDeleteModal"
      title="Confirmer la suppression"
      size="sm"
      @update:isVisible="showDeleteModal = false"
    >
      <template #body>
        <div class="text-center">
          <div class="mb-4">
            <i class="fas fa-exclamation-triangle text-red-500 text-4xl"></i>
          </div>
          <p class="text-black mb-2">
            Êtes-vous sûr de vouloir supprimer la marque
          </p>
          <p class="font-semibold text-black mb-4">
            "{{ brandToDelete?.name }}" ?
          </p>
          <p class="text-sm text-black">
            Cette action est irréversible.
          </p>
        </div>
      </template>
      
      <template #footer>
        <div class="flex justify-end gap-3">
          <ActionButton
            label="Annuler"
            variant="secondary"
            @click="showDeleteModal = false"
          />
          <ActionButton
            label="Supprimer"
            variant="danger"
            @click="confirmDelete"
          />
        </div>
      </template>
    </ModalComp>

    <ModalComp
      :isVisible="showReassignModal"
      title="Réassigner les produits"
      size="md"
      @update:isVisible="closeReassignModal"
    >
      <template #body>
        <div>
          <div class="mb-4 text-center">
            <i class="fas fa-exchange-alt text-amber-500 text-4xl"></i>
          </div>
          <p class="text-black mb-2 text-center">
            La marque <span class="font-semibold">"{{ brandToDelete?.name }}"</span>
            est associée à
            <span class="font-semibold">{{ brandToDelete?.productCount ?? 0 }} produit(s)</span>.
          </p>
          <p class="text-sm text-black mb-4 text-center">
            Choisissez une autre marque pour réassigner tous les produits concernés avant la suppression.
          </p>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Marque de destination <span class="text-red-500">*</span>
          </label>
          <select
            v-model="targetBrandId"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="" disabled>Sélectionner une marque</option>
            <option
              v-for="brand in availableTargetBrands"
              :key="brand.id"
              :value="brand.id"
            >
              {{ brand.name }}{{ brand.isDefault ? ' (par défaut)' : '' }}
            </option>
          </select>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end gap-3">
          <ActionButton
            label="Annuler"
            variant="secondary"
            @click="closeReassignModal"
          />
          <ActionButton
            label="Réassigner et supprimer"
            variant="danger"
            :disabled="!targetBrandId || isDeleting"
            :loading="isDeleting"
            @click="confirmDeleteWithReassign"
          />
        </div>
      </template>
    </ModalComp>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { PageHeader, ActionButton, BaseInput, ModalComp, ReusableTable } from '@/components'
import BrandStatusSwitch from '@/components/account/BrandStatusSwitch.vue'
import { getVendeurMarques, createVendeurMarque, updateVendeurMarque, deleteVendeurMarque, activerVendeurMarques, setVendeurMarqueStatut } from '@/api/brands'
import type { Brand } from '@/types'
import {useAuthStore} from "@/stores/auth";
import {useRouter} from "vue-router";
import { useNotification } from '@/composables/useNotification'

const authStore = useAuthStore()
const router = useRouter()
const notification = useNotification()

const useBrandStore = () => {
  const brands = ref<Brand[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const fetchBrands = async (throwOnError = false) => {
    isLoading.value = true
    error.value = null
    
    try {
      const marques = await getVendeurMarques()
      // Trier les marques : marque par défaut en premier, puis par nom
      brands.value = marques.sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1
        if (!a.isDefault && b.isDefault) return 1
        return a.name.localeCompare(b.name)
      })

      // Sécurité: s'il y a des marques mais aucune par défaut, définir la première comme défaut
      if (brands.value.length > 0 && !brands.value.some(b => b.isDefault)) {
        try {
          const first = brands.value[0]
          const updated = await updateVendeurMarque(first.id, undefined, true)
          brands.value[0] = {
            ...first,
            ...updated,
            isActive: first.isActive,
            productCount: first.productCount
          }
          // Mettre les autres en non défaut dans l'état local
          for (let i = 1; i < brands.value.length; i++) brands.value[i].isDefault = false
        } catch (e) {
          console.warn('Impossible de définir une marque par défaut automatiquement:', e)
        }
      }
    } catch (err) {
      error.value = 'Erreur lors du chargement des marques'
      console.error('Erreur fetchBrands:', err)
      if (throwOnError) throw err
    } finally {
      isLoading.value = false
    }
  }

  const addBrand = async (brandData: { name: string; isDefault: boolean }) => {
    try {
      // Si c'est la première marque, elle doit être par défaut
      if (brands.value.length === 0) {
        brandData.isDefault = true
      }
      const newBrand = await createVendeurMarque(brandData.name, brandData.isDefault)
      await fetchBrands(true)
      return newBrand
    } catch (err) {
      error.value = 'Erreur lors de l\'ajout de la marque'
      throw err
    }
  }

  const updateBrand = async (id: string, brandData: Partial<Brand>) => {
    try {
      // Empêcher d'enlever le statut défaut si c'est la seule marque par défaut
      if (brandData.isDefault === false) {
        const target = brands.value.find(b => b.id === id)
        const defaults = brands.value.filter(b => b.isDefault)
        if (target?.isDefault && defaults.length === 1) {
          notification.error('Il doit y avoir au moins une marque par défaut. Définissez une autre marque comme défaut avant de modifier celle-ci.')
          return false
        }
      }
      await updateVendeurMarque(id, brandData.name, brandData.isDefault)
      await fetchBrands(true)
      return true
    } catch (err) {
      error.value = 'Erreur lors de la modification'
      throw err
    }
  }

  const deleteBrand = async (id: string, targetBrandId?: string) => {
    try {
      // Interdire la suppression si c'est la dernière marque restante
      if (brands.value.length <= 1) {
        throw new Error('Vous ne pouvez pas supprimer votre dernière marque. Il doit y avoir au moins une marque par défaut.')
      }
      await deleteVendeurMarque(id, targetBrandId)
      await fetchBrands(true)
    } catch (err) {
      error.value = 'Erreur lors de la suppression'
      throw err
    }
  }

  return {
    brands,
    isLoading,
    error,
    fetchBrands,
    addBrand,
    updateBrand,
    deleteBrand
  }
}

const {
  brands,
  fetchBrands,
  addBrand,
  updateBrand,
  deleteBrand
} = useBrandStore()

const showModal = ref(false)
const showDeleteModal = ref(false)
const showReassignModal = ref(false)
const isEditing = ref(false)
const editingBrandId = ref<string | null>(null)
const brandToDelete = ref<Brand | null>(null)
const targetBrandId = ref('')
const isSubmitting = ref(false)
const isDeleting = ref(false)

const availableTargetBrands = computed(() => {
  if (!brandToDelete.value) return brands.value
  return brands.value.filter(b => b.id !== brandToDelete.value!.id)
})

const newBrand = reactive({
  name: '',
  isDefault: false
})

const modalTitle = computed(() => {
  return isEditing.value ? 'Modifier la marque' : 'Ajouter une nouvelle marque'
})

const brandColumns = [
  { key: 'number', title: '#', sortable: true, align: 'center' as const },
  { key: 'name', title: 'Nom de la marque', sortable: true, align: 'left' as const },
  { key: 'productCount', title: 'Nombre de produits', sortable: true, align: 'center' as const },
  { key: 'isDefault', title: 'Défaut', sortable: true, align: 'center' as const },
  { key: 'isActive', title: 'Statut', sortable: true, align: 'center' as const },
]

const isActivating = ref(false)
const statutUpdatingId = ref<string | null>(null)

const handleSetBrandStatus = async (brand: Brand, actif: boolean) => {
  if (statutUpdatingId.value || brand.isActive === actif) return
  statutUpdatingId.value = brand.id
  try {
    await setVendeurMarqueStatut(brand.id, actif)
    const target = brands.value.find((item) => item.id === brand.id)
    if (target) target.isActive = actif
    notification.success(actif ? 'Marque activée.' : 'Marque désactivée.')
  } catch (err) {
    notification.error(err instanceof Error ? err.message : 'Erreur lors du changement de statut.')
  } finally {
    statutUpdatingId.value = null
  }
}

const handleActivateBrands = async () => {
  if (isActivating.value || brands.value.length === 0) return
  isActivating.value = true
  try {
    const result = await activerVendeurMarques()
    await fetchBrands(true)
    if (result.statutBoutique === 1) {
      notification.success(
        result.nombreActivees > 0
          ? `${result.nombreActivees} marque(s) activée(s). Les marques sans produit visible restent désactivées.`
          : 'Aucune marque activée : aucun produit visible.',
      )
    } else {
      notification.success('La boutique est hors ligne. Toutes les marques ont été désactivées.')
    }
  } catch (err) {
    notification.error(err instanceof Error ? err.message : 'Erreur lors de l’activation des marques.')
  } finally {
    isActivating.value = false
  }
}

const openAddBrandModal = () => {
  isEditing.value = false
  editingBrandId.value = null
  showModal.value = true
  resetForm()
}

const editBrand = (row: Brand) => {
  isEditing.value = true
  editingBrandId.value = row.id
  newBrand.name = row.name
  newBrand.isDefault = row.isDefault
  showModal.value = true
}

const resetForm = () => {
  newBrand.name = ''
  newBrand.isDefault = false
}

const handleSubmit = async () => {
  if (isSubmitting.value) return
  const nameTrimmed = newBrand.name.trim()
  if (!nameTrimmed) {
    notification.error('Le nom de la marque est requis.')
    return
  }

  // Empêcher les doublons (insensible à la casse et aux espaces)
  const exists = brands.value.some(b => b.name.trim().toLowerCase() === nameTrimmed.toLowerCase() && (!isEditing.value || b.id !== editingBrandId.value))
  if (!isEditing.value && exists) {
    notification.error('Cette marque existe déjà dans votre liste.')
    return
  }
  if (isEditing.value && exists) {
    notification.error('Une autre marque avec ce nom existe déjà.')
    return
  }

  isSubmitting.value = true
  try {
    if (isEditing.value && editingBrandId.value) {
      const updated = await updateBrand(editingBrandId.value, {
        name: nameTrimmed,
        isDefault: newBrand.isDefault
      })
      if (updated === false) return
    } else {
      await addBrand({
        name: nameTrimmed,
        isDefault: newBrand.isDefault
      })
    }
    
    showModal.value = false
    resetForm()
  } catch (err) {
    console.error('Erreur lors de la soumission:', err)
  } finally {
    isSubmitting.value = false
  }
}

const handleDeleteBrand = (row: Brand) => {
  // Protection : empêcher la suppression de la marque par défaut
  if (row.isDefault) {
    notification.error('La marque par défaut ne peut pas être supprimée. Veuillez d\'abord définir une autre marque comme défaut.')
    return
  }
  // Protection : empêcher la suppression de la dernière marque
  if (brands.value.length <= 1) {
    notification.error('Vous ne pouvez pas supprimer votre dernière marque. Il doit y avoir au moins une marque par défaut.')
    return
  }

  brandToDelete.value = row
  targetBrandId.value = ''

  if ((row.productCount ?? 0) > 0) {
    const defaultBrand = brands.value.find(b => b.isDefault)
    if (defaultBrand && defaultBrand.id !== row.id) {
      targetBrandId.value = defaultBrand.id
    } else {
      const firstOther = availableTargetBrands.value[0]
      targetBrandId.value = firstOther?.id ?? ''
    }
    showReassignModal.value = true
  } else {
    showDeleteModal.value = true
  }
}

const closeReassignModal = () => {
  showReassignModal.value = false
  brandToDelete.value = null
  targetBrandId.value = ''
}

const confirmDelete = async () => {
  if (!brandToDelete.value) return

  if (brandToDelete.value.isDefault) {
    notification.error('La marque par défaut ne peut pas être supprimée.')
    showDeleteModal.value = false
    brandToDelete.value = null
    return
  }

  isDeleting.value = true
  try {
    await deleteBrand(brandToDelete.value.id)
    showDeleteModal.value = false
    brandToDelete.value = null
    notification.success('Marque supprimée avec succès.')
  } catch (err) {
    console.error('Erreur lors de la suppression:', err)
    notification.error(err instanceof Error ? err.message : 'Erreur lors de la suppression de la marque.')
  } finally {
    isDeleting.value = false
  }
}

const confirmDeleteWithReassign = async () => {
  if (!brandToDelete.value || !targetBrandId.value) {
    notification.error('Veuillez sélectionner une marque de destination.')
    return
  }

  isDeleting.value = true
  try {
    await deleteBrand(brandToDelete.value.id, targetBrandId.value)
    closeReassignModal()
    notification.success('Produits réassignés et marque supprimée avec succès.')
  } catch (err) {
    console.error('Erreur lors de la suppression avec réassignation:', err)
    notification.error(err instanceof Error ? err.message : 'Erreur lors de la suppression de la marque.')
  } finally {
    isDeleting.value = false
  }
}

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  fetchBrands()
})
</script>

<style lang="scss" scoped></style>
